import Vue from 'vue'
import { api } from 'src/plugins/axios'
import { addressKey, isCheckable } from 'src/utils/addressVerification'

/**
 * Google address verification results, cached by address.
 *
 * Shared by every chip on the page, so the same address typed for two
 * students (or synced into a second session) is checked once.
 *
 * Each entry is { status, suggestion, token }, where status is one of:
 *   checking     request in flight
 *   verified     Google confirmed it as entered
 *   fix          Google confirmed only its corrected version (`suggestion`)
 *   unverified   Google could not confirm it
 *   unavailable  Google could not be reached; worth a retry
 */
// In-flight requests by address key, so a caller that needs the final result
// (the class save) can wait on a check a blur already started.
const inflight = {}

export default {
  namespaced: true,
  state: {
    checks: {},
  },
  getters: {
    checkFor: state => address => state.checks[addressKey(address)] || null,
  },
  mutations: {
    setCheck (state, { key, check }) {
      Vue.set(state.checks, key, check)
    },
  },
  actions: {
    /**
     * Mark addresses already known to be verified (loaded from the server)
     * without asking Google again.
     */
    seed ({ commit }, { addresses, status = 'verified' }) {
      addresses.forEach(address => {
        commit('setCheck', { key: addressKey(address), check: { status, suggestion: null, token: null } })
      })
    },

    /**
     * Check an address with Google, unless it already has a settled result.
     * `force` re-asks, for a retry or a "check again".
     *
     * @returns the cache entry
     */
    async check ({ state, commit }, { address, force = false }) {
      if (!isCheckable(address)) return null

      const key = addressKey(address)
      if (inflight[key]) return inflight[key]

      // A settled result stands unless asked again. An outage is never
      // settled: the next blur or save tries again.
      const existing = state.checks[key]
      if (existing && !force && existing.status !== 'unavailable') return existing

      inflight[key] = request({ commit }, key, address)
      try {
        return await inflight[key]
      } finally {
        delete inflight[key]
      }
    },
  },
}

async function request ({ commit }, key, address) {
  commit('setCheck', { key, check: { status: 'checking', suggestion: null, token: null } })

  let check
  try {
    const { data } = await api.post('/address/validate', address)
    if (!data || !data.success) throw new Error((data && data.message) || 'No response')

    const { status, standardized, verificationToken } = data.payload
    check = {
      status,
      suggestion: status === 'fix' ? standardized : null,
      token: verificationToken || null,
    }
  } catch (err) {
    console.log('address check failed', err)
    check = { status: 'unavailable', suggestion: null, token: null }
  }

  commit('setCheck', { key, check })

  // A `fix` token vouches for Google's version, which is what the row
  // will hold once the user accepts it.
  if (check.status === 'fix' && check.suggestion) {
    commit('setCheck', {
      key: addressKey(check.suggestion),
      check: { status: 'verified', suggestion: null, token: check.token },
    })
  }

  return check
}
